import { useTranslation } from 'react-i18next';
import { useViewContext } from '../../context/ViewContext';
import {
  comparisonSummary,
  definitionsFor,
  filtersChipText,
  formatRange,
  screenLabel,
} from '../../lib/contextSummary';
import { screenCapabilities } from '../../lib/viewContext';
import { Icon } from '../shared/Icon';

/**
 * The context of the screen on display, compact and read-only: the top of
 * maître d'AI's panel and of Data sources say which venues, period,
 * comparison and filters the figures next to them count, in the bar's words.
 * Changing it happens in the context bar. Null outside OpsCommand.
 */
export function ContextLine({ variant = 'maitre' }: { variant?: 'maitre' | 'sources' }) {
  const { t } = useTranslation('chrome');
  const ctx = useViewContext();
  if (!ctx) return null;
  const { request, store } = ctx;
  const screen = request.screen;
  const uses = screenCapabilities(screen).uses;
  const count = request.venues.ids.length;
  const venues =
    count > 1 ? `${request.venues.label} · ${t('context.venues.count', { count })}` : request.venues.label;
  const parts: { key: string; text: string; icon?: string }[] = [{ key: 'venues', text: venues, icon: 'utensils' }];
  if (request.focus) parts.push({ key: 'focus', text: `› ${request.focus.label}` });
  if (variant === 'sources') {
    return (
      <p className="cx-line is-sources" aria-label={t('context.sources.aria')}>
        <span className="cx-line-part is-strong">
          <Icon name="utensils" size={12} />
          {venues}
        </span>
        <span className="cx-line-part">{t('context.sources.line')}</span>
      </p>
    );
  }
  if (uses.period) {
    parts.push({ key: 'period', text: formatRange(request.period.start, request.period.end), icon: 'calendar' });
  }
  if (uses.comparison) {
    const c = comparisonSummary(request.comparison, store.state.perfSettings);
    parts.push({ key: 'comparison', text: c.none ? c.text : `${c.text} · ${c.kind}` });
  }
  const filters = filtersChipText(screen, store.state);
  if (filters) parts.push({ key: 'filters', text: filters, icon: 'sliders' });
  const definitions = definitionsFor(screen, store.state);
  return (
    <div className="cx-line" role="group" aria-label={t('context.compactAria')}>
      <span className="cx-line-screen">{screenLabel(screen)}</span>
      {parts.map(part => (
        <span key={part.key} className={`cx-line-part${part.key === 'venues' ? ' is-strong' : ''}`}>
          {part.icon ? <Icon name={part.icon} size={12} /> : null}
          {part.text}
        </span>
      ))}
      <span className="cx-line-part is-defs">
        <Icon name="info" size={12} />
        {definitions.chip}
      </span>
    </div>
  );
}
